import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { serverDomain } from "../../config";
import "../../styles/OffersPage.css";

const Modal = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="fl-modal-overlay" onMouseDown={onClose}>
      <div className="fl-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fl-modal__head">
          <div className="fl-modal__title">{title}</div>
          <button className="fl-modal__close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="fl-modal__body">{children}</div>

        {footer ? <div className="fl-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

const OffersPage = () => {
  const navigate = useNavigate();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const [selectedOffer, setSelectedOffer] = useState(null);

  const [hasAccessToOffer3, setHasAccessToOffer3] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [trafficSource, setTrafficSource] = useState("");
  const [expectedVolume, setExpectedVolume] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${serverDomain}/api/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Ошибка при получении офферов");
      const data = await response.json();
      setOffers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const checkOfferRequestStatus = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${serverDomain}/api/offers/check_offer3_access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json().catch(() => ({}));
      setHasAccessToOffer3(!!result.hasAccess);
    } catch (err) {
      console.error("Ошибка при проверке доступа к офферу 3:", err);
    }
  };

  useEffect(() => {
    fetchOffers();
    checkOfferRequestStatus();
  }, []);

  const categories = useMemo(() => {
    const uniq = Array.from(
      new Set(
        offers
          .map((o) => (o.category || "").trim())
          .filter(Boolean)
      )
    );
    return ["all", ...uniq];
  }, [offers]);

  const filteredOffers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return offers.filter((o) => {
      const byName = !q || (o.name || "").toLowerCase().includes(q);
      const byCat = category === "all" || (o.category || "") === category;
      return byName && byCat;
    });
  }, [offers, query, category]);

  const handleCreateStream = () => navigate("/streams");

  const openRequest = (offerId) => {
    setSelectedOfferId(offerId);
    setTrafficSource("");
    setExpectedVolume("");
    setRequestModalOpen(true);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${serverDomain}/api/offers/offer_request`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          offer_id: selectedOfferId,
          traffic_source: trafficSource,
          expected_volume: expectedVolume,
        }),
      });

      if (res.ok) {
        alert("Заявка отправлена!");
        setRequestModalOpen(false);
        await checkOfferRequestStatus();
        return;
      }

      const errorData = await res.json().catch(() => ({}));
      if (errorData.error === "Вы уже отправили заявку на этот оффер") {
        alert("Вы уже отправили заявку на этот оффер");
      } else {
        alert("Ошибка при отправке заявки");
      }
    } catch (err) {
      console.error(err);
      alert("Ошибка при отправке заявки");
    }
  };

  return (
    <div>
      <TopBar pageTitle={"Офферы"} />
      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-offers">
            {/* Панель поиска (как на макете: компактная строка) */}
            <div className="fl-toolbar fl-toolbar--offers">
              <input
                className="fl-input fl-input--wide"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Название"
              />

              <select
                className="fl-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="all">Все</option>
                {categories
                  .filter((c) => c !== "all")
                  .map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
              </select>

              <button className="fl-btn" onClick={fetchOffers} disabled={loading}>
                {loading ? "Загрузка..." : "Обновить"}
              </button>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loader" />
              </div>
            ) : (
              <>
                {/* desktop: таблица */}
                <div className="fl-offers-panel fl-only-desktop">
                  <div className="fl-table-wrap">
                    <table className="fl-table">
                      <thead>
                        <tr>
                          <th>Название</th>
                          <th>Выплата</th>
                          <th className="fl-hide-sm">Категория</th>
                          <th style={{ width: 220 }}>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOffers.map((offer) => (
                          <tr key={offer.id}>
                            <td>
                              <div className="fl-offer-name">
                                <div className="fl-offer-name__title">{offer.name}</div>
                                <button
                                  className="fl-linkbtn"
                                  onClick={() => setSelectedOffer(offer)}
                                  type="button"
                                >
                                  Подробнее
                                </button>
                              </div>
                            </td>
                            <td>{offer.payout}</td>
                            <td className="fl-hide-sm">{offer.category}</td>
                            <td>
                              {offer.id === 3 && !hasAccessToOffer3 ? (
                                <button className="fl-btn" onClick={() => openRequest(offer.id)}>
                                  Получить доступ
                                </button>
                              ) : (
                                <button className="fl-btn" onClick={handleCreateStream}>
                                  Создать поток
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}

                        {filteredOffers.length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: 16, opacity: 0.7 }}>
                              Ничего не найдено
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* mobile: карточки */}
                <div className="fl-only-mobile">
                  <div className="fl-offer-cards">
                    {filteredOffers.map((offer) => (
                      <div className="fl-offer-card" key={offer.id}>
                        <div className="fl-offer-card__top">
                          <div className="fl-offer-card__title">{offer.name}</div>
                          <button className="fl-linkbtn" onClick={() => setSelectedOffer(offer)} type="button">
                            Подробнее
                          </button>
                        </div>

                        <div className="fl-offer-card__meta">
                          <div>
                            <span>Выплата</span>
                            <b>{offer.payout}</b>
                          </div>
                          <div>
                            <span>Категория</span>
                            <b>{offer.category}</b>
                          </div>
                        </div>

                        <div className="fl-offer-card__actions">
                          {offer.id === 3 && !hasAccessToOffer3 ? (
                            <button className="fl-btn fl-btn--wide" onClick={() => openRequest(offer.id)}>
                              Получить доступ
                            </button>
                          ) : (
                            <button className="fl-btn fl-btn--wide" onClick={handleCreateStream}>
                              Создать поток
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {filteredOffers.length === 0 && (
                      <div className="fl-empty">Ничего не найдено</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Подробнее */}
      <Modal
        open={!!selectedOffer}
        title={selectedOffer?.name || "Оффер"}
        onClose={() => setSelectedOffer(null)}
        footer={<button className="fl-btn" onClick={() => setSelectedOffer(null)}>Закрыть</button>}
      >
        {selectedOffer && (
          <>
            <div className="fl-kv">
              <div className="fl-kv__row">
                <span>Модель оплаты</span>
                <b>{selectedOffer.payout || "—"}</b>
              </div>
              <div className="fl-kv__row">
                <span>Категория</span>
                <b>{selectedOffer.category || "—"}</b>
              </div>
            </div>

            <div
              className="fl-desc"
              dangerouslySetInnerHTML={{
                __html: (selectedOffer.description || "Описание отсутствует").replace(/\n/g, "<br>"),
              }}
            />
          </>
        )}
      </Modal>

      {/* Запрос доступа */}
      <Modal
        open={requestModalOpen}
        title={"Запрос на доступ к приватному офферу"}
        onClose={() => setRequestModalOpen(false)}
        footer={
          <div className="fl-modal__footerRow">
            <button className="fl-btn fl-btn--ghost" onClick={() => setRequestModalOpen(false)} type="button">
              Отмена
            </button>
            <button className="fl-btn" onClick={submitRequest} type="button">
              Отправить
            </button>
          </div>
        }
      >
        <form onSubmit={submitRequest} className="fl-form">
          <label className="fl-label">
            Откуда будете привлекать аудиторию?
            <input
              className="fl-input fl-input--wide"
              required
              value={trafficSource}
              onChange={(e) => setTrafficSource(e.target.value)}
            />
          </label>

          <label className="fl-label">
            Какие предположительные объемы в день?
            <input
              className="fl-input fl-input--wide"
              required
              value={expectedVolume}
              onChange={(e) => setExpectedVolume(e.target.value)}
            />
          </label>

          {/* чтобы Enter срабатывал */}
          <button type="submit" style={{ display: "none" }} />
        </form>
      </Modal>
    </div>
  );
};

export default OffersPage;